import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { AdvShell } from "@/components/AdvShell";
import { Metric, money, num, StatusBadge } from "./advUtils";

export default function AdvAnalytics() {
  const [params, setParams] = useSearchParams();
  const [camps, setCamps] = useState([]);
  const [sel, setSel] = useState(params.get("c") || "");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/adv/campaigns").then((r) => { setCamps(r.data); if (!sel && r.data[0]) setSel(r.data[0].id); }).catch(() => {}); }, []);
  useEffect(() => { if (sel) { api.get(`/adv/campaigns/${sel}/analytics`).then((r) => setData(r.data)).catch(() => {}); setParams({ c: sel }); } }, [sel]);

  const c = data?.campaign;
  const daily = data?.daily || [];
  const maxImp = Math.max(1, ...daily.map((d) => d.impressions));

  return (
    <AdvShell>
      <h1 className="font-heading text-4xl tracking-tight mb-5" data-testid="adv-analytics-title">Analytics</h1>
      {camps.length === 0 ? (
        <div className="rounded-lg border border-dashed border-race-line py-16 text-center text-race-sub">No campaigns to analyze yet. <Link to="/advertiser/campaigns/new" className="text-race-volt hover:underline">Create one →</Link></div>
      ) : (
        <>
          <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="adv-analytics-select"
            className="bg-race-bg border border-race-line rounded-md px-3 py-2.5 text-sm text-race-txt mb-6 max-w-md w-full">
            {camps.map((x) => <option key={x.id} value={x.id}>{`${x.name} — ${x.status_label}`}</option>)}
          </select>

          {c && (
            <>
              <div className="flex items-center gap-3 mb-4"><span className="font-heading text-2xl">{c.name}</span><StatusBadge status={c.status} label={c.status_label} /></div>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                <Metric testid="adv-a-impr" label="Impressions" value={num(c.impressions)} />
                <Metric testid="adv-a-clicks" label="Clicks" value={num(c.clicks)} />
                <Metric testid="adv-a-ctr" label="CTR" value={`${c.ctr}%`} />
                <Metric testid="adv-a-spend" label="Spend" value={money(c.spend_usd)} sub={`Billed ${money(c.billed_usd)}`} />
                <Metric testid="adv-a-remaining" label="Remaining" value={money(c.remaining_usd)} />
              </div>

              <div className="bg-race-surface border border-race-line rounded-lg p-5">
                <div className="text-sm font-semibold mb-4">Performance over time</div>
                {daily.length === 0 ? (
                  <div className="text-race-sub text-sm py-8 text-center" data-testid="adv-analytics-nodata">No delivery data yet.</div>
                ) : (
                  <div className="flex items-end gap-1.5 h-48" data-testid="adv-analytics-chart">
                    {daily.map((d) => (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                        <div className="w-full bg-race-volt/70 rounded-t hover:bg-race-volt transition-colors relative" style={{ height: `${(d.impressions / maxImp) * 100}%`, minHeight: 2 }}
                          title={`${d.date}: ${d.impressions} impr, ${d.clicks} clicks`} />
                        <div className="text-[9px] text-race-muted rotate-45 origin-left whitespace-nowrap mt-1">{d.date.slice(5)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </AdvShell>
  );
}
